import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { scaleBand, scaleSequential } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { select } from "d3-selection";
import { interpolateBlues, interpolateViridis } from "d3-scale-chromatic";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type HeatmapData = {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    cells: Array<{
        x: string;
        y: string;
        value: number;
    }>;
    xCategories: string[];
    yCategories: string[];
    minValue: number;
    maxValue: number;
};

export const isHeatmapData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());
    const requiredColumns = ["heatmap_x", "heatmap_y", "heatmap_value", "heatmap_title", "heatmap_x_axis_label", "heatmap_y_axis_label"];

    const hasAllColumns = requiredColumns.every(reqCol => columnTitles.includes(reqCol));

    // Check for exact column count
    if (columns.length === 6 && hasAllColumns) return true;

    return false;
};

export const extractHeatmapData = (columns: any[], dataSource: any[]): HeatmapData | null => {
    if (!isHeatmapData(columns, dataSource)) {
        return null;
    }

    const xCol = columns.find(col => (col.title || "").trim() === "heatmap_x");
    const yCol = columns.find(col => (col.title || "").trim() === "heatmap_y");
    const valueCol = columns.find(col => (col.title || "").trim() === "heatmap_value");
    const titleCol = columns.find(col => (col.title || "").trim() === "heatmap_title");
    const xAxisLabelCol = columns.find(col => (col.title || "").trim() === "heatmap_x_axis_label");
    const yAxisLabelCol = columns.find(col => (col.title || "").trim() === "heatmap_y_axis_label");

    if (!xCol || !yCol || !valueCol || !titleCol || !xAxisLabelCol || !yAxisLabelCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();
    const xAxisLabel = String(dataSource[0]?.[xAxisLabelCol.dataIndex] || "").trim();
    const yAxisLabel = String(dataSource[0]?.[yAxisLabelCol.dataIndex] || "").trim();

    // Build a map of X/Y combinations to values (use last value if duplicates)
    const cellMap = new Map<string, number>();
    const xCategorySet = new Set<string>();
    const yCategorySet = new Set<string>();
    const allValues: number[] = [];

    dataSource.forEach(row => {
        const x = String(row[xCol.dataIndex] || "").trim();
        const y = String(row[yCol.dataIndex] || "").trim();
        const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
        const value = parseFloat(valueStr) || 0;

        if (x && y) {
            const key = `${x}|${y}`;
            cellMap.set(key, value); // Last value wins if duplicates
            xCategorySet.add(x);
            yCategorySet.add(y);
            allValues.push(value);
        }
    });

    const xCategories = Array.from(xCategorySet).sort();
    const yCategories = Array.from(yCategorySet).sort();

    // Build cells array from the map
    const cells: Array<{ x: string; y: string; value: number }> = [];
    xCategories.forEach(x => {
        yCategories.forEach(y => {
            const key = `${x}|${y}`;
            const value = cellMap.get(key);
            if (value !== undefined) {
                cells.push({ x, y, value });
            }
        });
    });

    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

    return {
        title,
        xAxisLabel: xAxisLabel || undefined,
        yAxisLabel: yAxisLabel || undefined,
        cells,
        xCategories,
        yCategories,
        minValue,
        maxValue
    };
};

export function Heatmap(props: {
    data: HeatmapData;
    darkMode: boolean;
    jobAlias?: string;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 1200);
                const chartHeight = Math.min(chartWidth * 0.625, 600);

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current || data.cells.length === 0) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        const margin = { top: 40, right: 150, bottom: 120, left: 120 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Set background to transparent
        svg.append("rect")
            .attr("width", dimensions.width)
            .attr("height", dimensions.height)
            .attr("fill", "transparent");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // X and Y scales
        const xScale = scaleBand()
            .domain(data.xCategories)
            .range([0, width])
            .padding(0.05);

        const yScale = scaleBand()
            .domain(data.yCategories)
            .range([0, height])
            .padding(0.05);

        // Color scale - use interpolateViridis for better accessibility, or interpolateBlues
        const colorScale = scaleSequential(darkMode ? interpolateViridis : interpolateBlues)
            .domain([data.minValue, data.maxValue]);

        // X-axis
        const xAxis = g.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(axisBottom(xScale));

        xAxis.selectAll("text")
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .attr("transform", null)
            .style("font-size", "12px")
            .style("text-anchor", "middle");

        // Y-axis
        const yAxis = g.append("g")
            .call(axisLeft(yScale));

        yAxis.selectAll("text")
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .style("font-size", "12px");

        // Axis lines
        g.selectAll(".domain, .tick line")
            .attr("stroke", darkMode ? "#666666" : "#cccccc");

        // X-axis label
        if (data.xAxisLabel) {
            g.append("text")
                .attr("x", width / 2)
                .attr("y", height + margin.bottom - 10)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(data.xAxisLabel);
        }

        // Y-axis label
        if (data.yAxisLabel) {
            g.append("text")
                .attr("transform", "rotate(-90)")
                .attr("y", -margin.left + 20)
                .attr("x", -height / 2)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(data.yAxisLabel);
        }

        // Heatmap cells
        const cells = g.selectAll(".cell")
            .data(data.cells)
            .enter()
            .append("rect")
            .attr("class", "cell")
            .attr("x", d => xScale(d.x) || 0)
            .attr("y", d => yScale(d.y) || 0)
            .attr("width", xScale.bandwidth())
            .attr("height", yScale.bandwidth())
            .attr("fill", d => colorScale(d.value))
            .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
            .attr("stroke-width", 1)
            .on("mouseover", function(this: SVGRectElement, event: MouseEvent, d: { x: string; y: string; value: number }) {
                select(this)
                    .attr("opacity", 0.8)
                    .attr("stroke-width", 2);

                // Show tooltip
                const tooltipText = `${d.x}, ${d.y}: ${d.value.toLocaleString()}`;
                const textLength = tooltipText.length * 6;
                const tooltipWidth = Math.max(textLength + 20, 80);

                const tooltip = g.append("g")
                    .attr("class", "tooltip")
                    .attr("transform", `translate(${(xScale(d.x) || 0) + xScale.bandwidth() / 2},${(yScale(d.y) || 0) - 15})`);

                tooltip.append("rect")
                    .attr("x", -tooltipWidth / 2)
                    .attr("y", -12)
                    .attr("width", tooltipWidth)
                    .attr("height", 20)
                    .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                    .attr("stroke", darkMode ? "#666666" : "#cccccc")
                    .attr("rx", 4);

                tooltip.append("text")
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "11px")
                    .attr("y", 2)
                    .text(tooltipText);
            })
            .on("mouseout", function(this: SVGRectElement) {
                select(this)
                    .attr("opacity", 1)
                    .attr("stroke-width", 1);
                g.selectAll(".tooltip").remove();
            });

        // Color legend
        const legendWidth = 20;
        const legendHeight = 200;
        const legendX = dimensions.width - margin.right - 100;
        const legendY = margin.top + 20;

        const legend = svg.append("g")
            .attr("class", "legend")
            .attr("transform", `translate(${legendX},${legendY})`);

        // Legend gradient definition
        const defs = svg.append("defs");
        const gradient = defs.append("linearGradient")
            .attr("id", "heatmap-gradient")
            .attr("x1", "0%")
            .attr("x2", "0%")
            .attr("y1", "100%")
            .attr("y2", "0%");

        // Create gradient stops
        const numStops = 10;
        for (let i = 0; i <= numStops; i++) {
            const offset = (i / numStops) * 100;
            const value = data.minValue + (data.maxValue - data.minValue) * (i / numStops);
            gradient.append("stop")
                .attr("offset", `${offset}%`)
                .attr("stop-color", colorScale(value));
        }

        // Legend rectangle with gradient
        legend.append("rect")
            .attr("width", legendWidth)
            .attr("height", legendHeight)
            .attr("fill", "url(#heatmap-gradient)")
            .attr("stroke", darkMode ? "#666666" : "#cccccc")
            .attr("stroke-width", 1);

        // Legend labels
        legend.append("text")
            .attr("x", legendWidth + 10)
            .attr("y", 0)
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .attr("font-size", "11px")
            .attr("text-anchor", "start")
            .attr("alignment-baseline", "hanging")
            .text(data.maxValue.toLocaleString());

        legend.append("text")
            .attr("x", legendWidth + 10)
            .attr("y", legendHeight)
            .attr("fill", darkMode ? "#ffffff" : "#000000")
            .attr("font-size", "11px")
            .attr("text-anchor", "start")
            .attr("alignment-baseline", "baseline")
            .text(data.minValue.toLocaleString());

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Set SVG dimensions
            svg.setAttribute('width', dimensions.width.toString());
            svg.setAttribute('height', dimensions.height.toString());
            svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background (transparent)
                    ctx.fillStyle = 'transparent';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    style={{ maxWidth: "100%", overflow: "visible" }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download heatmap as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download heatmap data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
}

